import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { CircularText } from './CircularText';
import TypewriterText from './TypewriterText';
import StaggeredWords from './StaggeredWords';
import MagneticButton from './MagneticButton';

interface VideoHeroProps {
  title: string;
  subtitle?: string;
  backgroundVideo?: string;
  backgroundImages: string[];
  primaryCTA?: {
    text: string;
    href: string;
    onClick?: () => void;
  };
  secondaryCTA?: {
    text: string;
    href: string;
    onClick?: () => void;
  };
  enableTypewriter?: boolean;
  enableStaggeredSubtitle?: boolean;
}

const VideoHero: React.FC<VideoHeroProps> = ({
  title,
  subtitle,
  backgroundVideo,
  backgroundImages,
  primaryCTA,
  secondaryCTA,
  enableTypewriter = true,
  enableStaggeredSubtitle = true
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [titleComplete, setTitleComplete] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  // Scroll-based parallax
  const { scrollY } = useScroll();
  const backgroundY = useTransform(scrollY, [0, 1000], [0, -300]);
  const contentY = useTransform(scrollY, [0, 1000], [0, -100]);

  // Spring animations for smooth parallax
  const smoothBackgroundY = useSpring(backgroundY, { stiffness: 100, damping: 30 });
  const smoothContentY = useSpring(contentY, { stiffness: 100, damping: 30 });

  // Background slideshow effect
  useEffect(() => {
    if (backgroundImages.length > 1 && !videoLoaded) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % backgroundImages.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [backgroundImages.length, videoLoaded]);

  const handleCTAClick = (cta: { href: string; onClick?: () => void }) => {
    if (cta.onClick) {
      cta.onClick();
    } else {
      window.location.href = cta.href;
    }
  };

  const handleVideoToggle = (shouldPlay: boolean) => {
    if (shouldPlay && !showVideo) {
      setShowVideo(true);
      // Wait for video to load then play
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = 1; // Start at 1 second
          videoRef.current.play();
          setIsVideoPlaying(true);
        }
      }, 100);
    } else if (videoRef.current) {
      if (shouldPlay) {
        videoRef.current.currentTime = 1; // Start at 1 second
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
      setIsVideoPlaying(shouldPlay);
    }
  };

  // Floating particles data
  const particles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 8 + 8,
    delay: Math.random() * 4,
  }));

  return (
    <section
      ref={containerRef}
      className="relative h-[100vh] flex items-center overflow-hidden -mt-16"
    >
      {/* Multi-layer Background System */}
      <div className="absolute inset-0">
        {/* Video Background - Only show when user clicks play */}
        {backgroundVideo && !videoError && showVideo && (
          <motion.video
            ref={videoRef}
            style={{ y: smoothBackgroundY }}
            className="absolute -inset-y-32 inset-x-0 w-full h-[calc(100%+16rem)] object-cover"
            muted
            loop
            playsInline
            onLoadedData={() => {
              setVideoLoaded(true);
              if (videoRef.current) {
                videoRef.current.currentTime = 1; // Set initial time to 1 second
              }
            }}
            onError={() => setVideoError(true)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <source src={backgroundVideo} type="video/mp4" />
          </motion.video>
        )}

        {/* Image Background - Always show initially */}
        {(!showVideo || videoError) && (
          <div className="absolute -inset-y-32 inset-x-0 h-[calc(100%+16rem)]">
            {backgroundImages.map((image, index) => (
              <motion.div
                key={index}
                style={{
                  y: smoothBackgroundY,
                  backgroundImage: `url(${image})`
                }}
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{
                  opacity: index === currentImageIndex ? 1 : 0,
                  scale: index === currentImageIndex ? 1 : 1.1
                }}
                transition={{
                  duration: 2,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-black/50" />

        {/* Central Play Button Overlay - Only show when video is not playing */}
        {/* {!showVideo && backgroundVideo && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center z-10"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
          >
            <motion.button
              onClick={() => handleVideoToggle(true)}
              className="relative w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/40 hover:bg-white/30 hover:border-white/60 transition-all duration-300 group shadow-2xl"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              animate={{
                boxShadow: [
                  "0 0 0 0 rgba(255, 255, 255, 0.3)",
                  "0 0 0 10px rgba(255, 255, 255, 0)",
                  "0 0 0 0 rgba(255, 255, 255, 0)"
                ]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <svg 
                className="w-10 h-10 text-white ml-1 group-hover:text-white/90" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z"/>
              </svg>
            </motion.button>
            <motion.div
              className="absolute bottom-4 text-white/80 text-sm font-medium"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.5 }}
            >
              Click to play video
            </motion.div>
          </motion.div>
        )} */}
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none">
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute bg-white/20 rounded-full"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
            }}
            animate={{
              y: [-15, 15, -15],
              x: [-8, 8, -8],
              opacity: [0.1, 0.6, 0.1],
            }}
            transition={{
              duration: particle.duration,
              delay: particle.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <motion.div
        style={{ y: smoothContentY }}
        className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-8 w-full pt-16"
      >
        <div className="flex items-center min-h-[80vh]">
          {/* Text Content - Left aligned on desktop, center on mobile */}
          <div className="text-center lg:text-left w-full lg:w-2/3">
            {/* Title with Typewriter Effect */}
            <div className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-6 leading-tight backdrop-blur-sm bg-black/20 rounded-2xl p-4 md:p-6 border border-white/10 shadow-xl inline-block">
              {enableTypewriter ? (
                <TypewriterText
                  text={title}
                  speed={50}
                  delay={0}
                  onComplete={() => setTitleComplete(true)}
                />
              ) : (
                <motion.h1
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 1.2,
                    ease: "easeOut",
                    delay: 0.2
                  }}
                >
                  {title}
                </motion.h1>
              )}
            </div>

            {/* Subtitle with Staggered Words */}
            {subtitle && (
              <div className="mb-8 backdrop-blur-sm bg-black/15 rounded-xl p-3 md:p-4 border border-white/10 shadow-lg inline-block">
                {enableStaggeredSubtitle && titleComplete ? (
                  <StaggeredWords
                    text={subtitle}
                    className="text-lg md:text-xl text-gray-200 leading-relaxed max-w-2xl mx-auto lg:mx-0"
                    delay={0.5}
                    staggerDelay={0.08}
                    animationType="fadeUp"
                  />
                ) : !enableTypewriter ? (
                  <motion.p
                    className="text-lg md:text-xl text-gray-200 leading-relaxed max-w-2xl mx-auto lg:mx-0"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 1,
                      ease: "easeOut",
                      delay: 0.6
                    }}
                  >
                    {subtitle}
                  </motion.p>
                ) : (
                  <div className="text-lg md:text-xl text-gray-200 leading-relaxed max-w-2xl mx-auto lg:mx-0 opacity-0">
                    {subtitle}
                  </div>
                )}
              </div>
            )}

            {/* CTA Buttons */}
            {(primaryCTA || secondaryCTA) && (
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
                initial={{ opacity: 0, y: 30 }}
                animate={{
                  opacity: enableTypewriter ? (titleComplete ? 1 : 0) : 1,
                  y: 0
                }}
                transition={{
                  duration: 0.8,
                  ease: "easeOut",
                  delay: enableTypewriter ? 2 : 1
                }}
              >
                {primaryCTA && (
                  <MagneticButton
                    onClick={() => handleCTAClick(primaryCTA)}
                    className="bg-gradient-to-r from-[#FF5A1F] to-[#FF6B35] text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-[#e4501c] hover:to-[#e55a2e] transition-all duration-300 flex items-center justify-center space-x-3 shadow-2xl group"
                    magneticStrength={0.4}
                    rippleColor="rgba(255, 255, 255, 0.3)"
                  >
                    <span>{primaryCTA.text}</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </MagneticButton>
                )}

                {secondaryCTA && (
                  <MagneticButton
                    onClick={() => handleCTAClick(secondaryCTA)}
                    className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-[#1F2937] transition-all duration-300 flex items-center justify-center space-x-3 shadow-2xl backdrop-blur-sm"
                    magneticStrength={0.3}
                    rippleColor="rgba(255, 255, 255, 0.2)"
                  >
                    <span>{secondaryCTA.text}</span>
                  </MagneticButton>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Video Play Button - Bottom Right on desktop, bottom center on mobile */}
      <motion.div
        className="absolute bottom-8 right-8 lg:right-8 lg:bottom-8 md:right-6 md:bottom-6 sm:right-4 sm:bottom-20 z-20"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: enableTypewriter ? (titleComplete ? 1 : 0) : 1,
          scale: 1
        }}
        transition={{
          duration: 0.8,
          ease: "easeOut",
          delay: enableTypewriter ? 2.5 : 1.5
        }}
      >
        <CircularText
          text="* Rolleston Homes *"
          onPlayToggle={handleVideoToggle}
          isPlaying={isVideoPlaying}
        />
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: enableTypewriter ? 3 : 1.5, duration: 0.8 }}
      >
        <motion.div
          className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div
            className="w-1 h-3 bg-white/70 rounded-full mt-2"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default VideoHero;